/**
 * useAutoCalculateDates Hook
 *
 * Custom hook that encapsulates business logic for automatic contract date calculation
 * Extracted from AutoCalculateDatesButton component to improve maintainability and testability
 *
 * Features:
 * - Birthdate validation
 * - Disabled state computation
 * - Success/error handling with toast notifications
 * - Integration with useContractDateCalculation mutation
 * - Full i18n support with Lingui
 */

import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { useLingui } from '@lingui/react';
import { t } from '@lingui/macro';

import { useContractDateCalculation } from '@/hooks/use-contract-date-calculation';
import type { ContractDateCalculationResponse } from '@/types/contract-dates';
import { isValidIsoDate, parseContractDates } from '@/utils/contracts/dateParsing';
import { showContractDatesToasts } from '@/utils/contracts/toasts';

/**
 * Props interface for useAutoCalculateDates hook
 */
interface UseAutoCalculateDatesProps {
  /**
   * The child's birthdate as an ISO date string (YYYY-MM-DD)
   * Required for the calculation
   */
  childBirthdate?: string;

  /**
   * The childcare location ID where the child will attend
   * Required for the calculation (used to determine canton)
   */
  childcareId?: string;

  /**
   * Callback when dates are successfully calculated and validated
   * @param startDate - The calculated contract start date
   * @param endDate - The calculated contract end date (null if open-ended)
   */
  onDatesCalculated: (startDate: Date, endDate: Date | null) => void;

  /**
   * Whether the calculation should be disabled
   * Automatically disabled if no childBirthdate or childcareId is provided
   */
  disabled?: boolean;
}

/**
 * Return interface for useAutoCalculateDates hook
 */
interface UseAutoCalculateDatesReturn {
  /**
   * Whether the calculation button should be disabled
   * True if: external disabled prop, no childcareId, invalid birthdate, or calculation pending
   */
  isDisabled: boolean;

  /**
   * Whether a calculation is currently in progress
   */
  isPending: boolean;

  /**
   * Handler to trigger the date calculation
   * Performs validation before calling the mutation
   */
  handleClick: () => void;
}

/**
 * Custom hook for automatic contract date calculation logic
 *
 * Encapsulates all business logic for the AutoCalculateDatesButton:
 * - Validates birthdate format
 * - Computes disabled state
 * - Handles calculation success with date parsing and toasts
 * - Handles button clicks with validation
 * - Integrates with useContractDateCalculation mutation
 *
 * @param props - Hook configuration options
 * @returns Object with isDisabled, isPending, and handleClick
 *
 * @example
 * ```typescript
 * const { isDisabled, isPending, handleClick } = useAutoCalculateDates({
 *   childBirthdate: '2020-05-15',
 *   childcareId: 'childcare-uuid',
 *   onDatesCalculated: (start, end) => {
 *     form.setValue('startDate', start);
 *     form.setValue('endDate', end);
 *   }
 * });
 * ```
 */
export function useAutoCalculateDates({
  childBirthdate,
  childcareId,
  onDatesCalculated,
  disabled = false,
}: UseAutoCalculateDatesProps): UseAutoCalculateDatesReturn {
  const { i18n } = useLingui();

  // Use the TanStack Query mutation hook
  // Silent: showContractDatesToasts reports the result, with the dates, in one toast.
  const { mutate: calculateDates, isPending } = useContractDateCalculation({ silent: true });

  // Validate if birthdate is a valid ISO date string
  const isValidBirthdate = useMemo(() => {
    return isValidIsoDate(childBirthdate);
  }, [childBirthdate]);

  // Determine if calculation should be disabled
  const isDisabled = useMemo(() => {
    return disabled || !childcareId || !isValidBirthdate || isPending;
  }, [disabled, childcareId, isValidBirthdate, isPending]);

  // Handle successful calculation
  const handleSuccess = useCallback(
    (response: ContractDateCalculationResponse) => {
      // Parse and validate dates with defensive error handling
      let parsedDates;

      try {
        parsedDates = parseContractDates(response);
      } catch (_error) {
        toast.error(
          i18n._(
            t({
              id: 'contracts.autoCalc.dateParseError',
              message: 'Invalid date format received',
            })
          )
        );
        return;
      }

      // Null guard to satisfy TypeScript strict mode
      if (!parsedDates.startDate) {
        return;
      }

      // Call the parent callback with potentially null endDate (start-only flows)
      onDatesCalculated(parsedDates.startDate, parsedDates.endDate);

      // One toast with all calculation information; the utility handles both
      // start+end and start-only scenarios
      showContractDatesToasts(i18n, parsedDates);
    },
    [onDatesCalculated, i18n]
  );

  // Handle button click
  const handleClick = useCallback(() => {
    // Validate childcareId
    if (!childcareId) {
      toast.error(
        i18n._(
          t({
            id: 'contracts.autoCalc.missingChildcare',
            message: 'Please select a childcare location first',
          })
        )
      );
      return;
    }

    // Validate childBirthdate
    if (!childBirthdate || !isValidBirthdate) {
      toast.error(
        i18n._(
          t({
            id: 'contracts.autoCalc.missingBirthdate',
            message: 'Please provide a valid child birthdate',
          })
        )
      );
      return;
    }

    // Trigger the calculation with validated birthdate and childcareId
    calculateDates(
      {
        childBirthdate,
        childcareId,
      },
      {
        onSuccess: handleSuccess,
        // Error handling is already done in the hook
      }
    );
  }, [childcareId, childBirthdate, isValidBirthdate, calculateDates, handleSuccess, i18n]);

  return {
    isDisabled,
    isPending,
    handleClick,
  };
}
